import { createApp, provide, h } from "vue"
import { createPinia } from "pinia";
import * as Sentry from "@sentry/vue";
import App from "./App.vue";

import router from "./router/routes";

// Template components
import BaseBlock from "@/components/BaseBlock.vue";
import BasePageHeading from "@/components/BasePageHeading.vue";

// Template directives
import tooltip from "@/directives/tooltip";
import linkify from "@/directives/linkify";
import vSelect from 'vue-select'

// Bootstrap framework
import * as bootstrap from "bootstrap";

import { DateTime } from "luxon";

window.bootstrap = bootstrap;
import { DefaultApolloClient } from "@vue/apollo-composable"
import { apolloClient } from "./apollo-client"
import VueRoughNotation from 'vue-rough-notation';
import { createHead } from '@unhead/vue';
import Vue3TagsInput from 'vue3-tags-input';
import CountryFlag from 'vue-country-flag-next'

const app = createApp({
    setup() {
        provide(DefaultApolloClient, apolloClient)
    },
    render: () => h(App),
})

// Set up the global error handler before sentry is initialized
app.config.errorHandler = (err, instance, info) => {
    // Handle the error globally
    console.error("Global error:", err);
    console.log("Vue instance:", instance);
    console.log("Error info:", info);
};

if (import.meta.env.VITE_SENTRY_AUTH_TOKEN) {
    console.log("Sentry enabled");
    Sentry.init({
        app,
        dsn: "https://5b5b555093d8275e55b86589f41c5b43@o298252.ingest.us.sentry.io/4507336385429504",
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration(),
        ],
        // Performance Monitoring
        tracesSampleRate: 1.0, //  Capture 100% of the transactions
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: ["localhost", /^https:\/\/pagewatch\.ai/],
        // Session Replay
        replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    });
}
const head = createHead()
app.use(head)
app.use(VueRoughNotation);
app.component('country-flag', CountryFlag)
app.config.globalProperties.$filters = {
    truncate(value, length = 36) {
        if (!value) {
            return ''
        }
        if (value.length >= length) {
            return value.substring(0, length) + '...'
        }
        return value

    },
    dateFilter(value) {
        const d = DateTime.fromISO(value);
        return d.toFormat("d MMM h:mm a")
    },
    dateRelative(value) {
        const d = DateTime.fromISO(value);
        return d.toRelative()
    },
    prettyNumber(num) {
        num = parseFloat(num);
        if (num < 1000) {
            return num.toString();
        }

        const abbreviations = ['', 'k', 'M', 'B', 'T'];
        let magnitude = 0;

        while (Math.abs(num) >= 1000 && magnitude < abbreviations.length - 1) {
            magnitude++;
            num /= 1000;
        }

        // Format with at most 1 decimal place if needed
        const formatted = Number.isInteger(num) ?
            Math.floor(num) :
            num.toFixed(1).replace(/\.0$/, '');

        return `${formatted}${abbreviations[magnitude]}`;
    }
}

// Use Pinia and Vue Router
app.use(createPinia());
// Register global components
app.component("BaseBlock", BaseBlock);
app.component("BasePageHeading", BasePageHeading);
app.component('vue3-tags-input', Vue3TagsInput)
    .component("v-select", vSelect);
// Register global directives
app.directive("tooltip", tooltip);
app.directive("linkify", linkify);

import VueApexCharts from "vue3-apexcharts";

app.use(VueApexCharts);


app.use(router);
router.beforeEach(async (to, from) => {
    if (to.meta.title) {
        document.title = typeof to.meta.title === 'function'
            ? to.meta.title(to)
            : to.meta.title;
    }
    // next();
})
// await router.isReady()
app.mount("#app");
